import { useMemo } from "react";
import HttpService from "./httpService";
import { useTranslation } from "react-i18next";
import gobalization from "../constants/globalization";
import { validateAddress } from "./addressValidationService";
import {
    GuestAccessTokenResponse,
    mintGuestAccessToken as requestGuestAccessToken,
} from "./guestAccessTokenService";
const useHttpService = () => {
    const enviromentURL = () => {
        let envURL = "https://api.store.nvidia.com";
        let host = window.location.hostname;
        if (host.split(".")[0] !== "store" && host !== "marketplace.nvidia.com") {
            envURL = `https://${host.split(".")[0].split("-")[0]}-api.store.nvidia.com`;
        }
        if (host.indexOf("localhost") > -1) {
            envURL = "http://localhost:5000";
        }
        return envURL;
    };

    const API_URL = useMemo(() => enviromentURL(), []);
    const httpService = useMemo(() => new HttpService(API_URL), [API_URL]);

    return httpService;
};


export const useCart = () => {
    const { t } = useTranslation();
    const httpService = useHttpService();
    const mintGuestAccessToken = (email: string): Promise<GuestAccessTokenResponse> =>
        requestGuestAccessToken(httpService, email);

    const getCart = async (handleSuccess: (result: any) => void, handleError: (err: string) => void) => {
        try {
            const result = await httpService.get("/ucc-software/v2/cart/active");
            let resultLength = Object.keys(result.cartItems).length;
            if (result && resultLength > 0) {
                handleSuccess(result);
            } else {
                handleError(t('YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY'));
            }
        } catch (er) {
            console.error(er);
            handleError(t('YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY'));
        }
    };

    const createCart = async (requestBody: any, headerObj: any, handleSuccess: () => void, handleError: (err: string) => void) => {
        try {
            const result = await httpService.postCart("/ucc-software/v2/cart/active", requestBody, headerObj);
            if (result === 200) {
                handleSuccess();
            } else {
                handleError(t('YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY'));
            }
        } catch (er) {
            console.error(er);
            handleError(t('YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY'));
        }
    }

    const updateCart = async (handleSuccess: (result: any, productId?: number) => void, handleError: (err: string, productId?: number) => void, productId?: string, selectedOption?: number, isDownload?: boolean, coupon?: string, currency?: any, couponApplied?: boolean | undefined) => {
        try {
            let headerObj = { "content-type": "application/json" };
            let cartBody = {}
            if (productId && (selectedOption !== undefined && selectedOption !== null)) {
                cartBody = { cart: { products: [{ productId: productId, quantity: selectedOption }], isDigital: isDownload } };
            } else if (currency) {
                cartBody = currency;
            } else if (couponApplied) {
                cartBody = { 'remove_coupon': couponApplied };
            } else {
                cartBody = { promoCode: coupon || '' };
            }
            const result = await httpService.put("/ucc-software/v2/cart/active", cartBody, headerObj);
            if (result) {
                if (productId) {
                    handleSuccess(result, Number(productId));
                } else {
                    handleSuccess(result);
                }
            }
        } catch (er) {
            console.error(er);
            if (productId) {
                handleError('', Number(productId));
            } else {
                handleError('');
            }
        }
    };

    const submitCart = async (order: any, handleCartSuccess: (result: any) => void, handleCartError: (err: any) => void) => {
        try {
            let headerObj = { "content-type": "application/json" };
            const cartBody = { cart: { ...order.cart, ...gobalization(order.locale, order.brand), isPurchasingForSomeoneElse: order.isPurchasingForSomeoneElse || false } }
            const result = await httpService.post("/ucc-software/v2/checkout", cartBody, headerObj);
            if (result) {
                handleCartSuccess(result);
            }
        } catch (er: any) {
            console.log(er, er?.error);
            // Check if error has specific code (e.g., postal_code_invalid)
            if (er && er.message.length) {
                let errMsg = JSON.parse(er?.message);
                if (errMsg && (errMsg?.error?.code === 'postal_code_invalid' || errMsg?.error?.code === 'SEATS_EXHAUSTED' || errMsg?.error?.code === 'CRS_ENROLLMENT_FAILED' || errMsg?.error?.code === 'invalid-coupon')) {
                    console.log('errMsg==>', errMsg);
                    handleCartError(errMsg?.error);
                } else {
                    handleCartError({ code: 'unknown_error', message: t('YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY') });
                }
            } else {
                handleCartError({ code: 'unknown_error', message: t('YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY') });
            }
        }
    }

    const submitCapatcha = async (orderToken: any, handleCapatchaSuccess: (result: any) => void, handleCapatchaError: () => void) => {
        try {
            let headerObj = { "content-type": "application/json" };
            let cartBody = { "event": orderToken };
            const result = await httpService.post("/ucc-software/v2/checkout/assessments", cartBody, headerObj);
            console.log("submitCapatcha==>", result);
            if (result) {
                handleCapatchaSuccess(result);
            }
        } catch (er) {
            console.log(er);
            handleCapatchaError();
        }
    }

    const getPaymentIntent = async (
        deviceFingerprintUuid: { request_uuid: string },
        handlePaymentIntentSuccess: (result: any) => void,
        handlePaymentIntentError: (err: any) => void
    ) => {
        try {
            let headerObj = { "content-type": "application/json" };
            const result = await httpService.post("/ucc-software/v2/checkout/authorize", deviceFingerprintUuid, headerObj);
            if (result) {
                handlePaymentIntentSuccess(result);
            } else {
                handlePaymentIntentError(t('CART_PAYMENT_FAILURE'));
            }
        } catch (er: any) {
            console.log(er);
            if (er && er.message.length) {
                let errMsg = JSON.parse(er?.message);
                if (errMsg && errMsg?.error?.code === 'invalid-coupon') {
                    console.log('errMsg==>', errMsg);
                    handlePaymentIntentError(errMsg?.error);
                } else {
                    handlePaymentIntentError({ code: 'unknown_error', message: t('YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY') });
                }
            } else {
                handlePaymentIntentError(er?.error);
            }
        }
    }

    const submitOrder = async (handleOrderSuccess: (result: any) => void, handleOrderError: (e: any) => void, paymentSuccess: boolean, eventDetail?: any) => {
        try {
            let headerObj = { "content-type": "application/json" };
            const result = await httpService.submitOrderPost("/ucc-software/v2/checkout/submit-cart", { paymentSuccess: paymentSuccess, eventDetail: eventDetail ? { ...eventDetail } : null }, headerObj);
            console.log('SUBMIT CART',)
            if (result) {
                handleOrderSuccess(result);
            }
        } catch (er: any) {
            console.log(er.error);
            if (er && er?.error) {
                handleOrderError(er?.error);
            } else {
                handleOrderError({ code: 'unknown_error', message: t('YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY') });
            }
        }
    }
    const submitAddress = async (address: any, handleAddressSuccess: (result: any) => void, handleAddressError: (err: any) => void) => {
        try {
            const result = await validateAddress(httpService, address);
            console.log("submitAddress==>", result);

            if (result) {
                handleAddressSuccess(result);
            }
        } catch (er) {
            console.log('submitAddress error==>', er);
            handleAddressError(er);
        }
    }
    return { getCart, createCart, updateCart, submitCart, submitCapatcha, submitOrder, submitAddress, getPaymentIntent, mintGuestAccessToken };
};